'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { MessageSquareWarning } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { resolveResultPages } from '../constants';
import type { OcrQaResult, OcrQaReview } from '../types';

interface OcrQaRejectionCommentsProps {
  result: OcrQaResult;
  /**
   * Show only the reasons that concern this GLOBAL page number. A reason belongs
   * under the page it is about — rendering a result's whole history in one place
   * put page 1's reasons under PG-002, so a page the reviewer had ACCEPTED
   * displayed a rejection block.
   */
  pageNumber?: number;
  /**
   * Also show the review's SHARED note — the container-level "general notes"
   * from the reject dialog, which is about the container rather than any one
   * page. Set on exactly one section per result, or it repeats on every page.
   */
  includeSharedNotes?: boolean;
}

interface RejectionEntry {
  review: OcrQaReview;
  /** Null for the review's shared/general note; set for a per-page reason. */
  pageLabel: string | null;
  text: string;
}

/**
 * Read-only display of QA rejection reasons for one result — the shared
 * general note (if any) plus one entry per physical page that has its own
 * `page_notes` reason. Newest review first, tagged with the rejecting tier,
 * reviewer, and timestamp. Rendered via the shared `OcrResultsPage`
 * `renderPageFooter` slot on both the QA detail pages and `ocr-containers/[id]`.
 */
export function OcrQaRejectionComments({
  result,
  pageNumber,
  includeSharedNotes
}: OcrQaRejectionCommentsProps) {
  const t = useTranslations('ocrQa');

  const pageLabelByNumber = useMemo(() => {
    const map = new Map<number, string>();
    for (const page of resolveResultPages(result)) {
      map.set(
        page.pageNumber,
        page.rawLabel
          ? t('pageSectionTitle', { number: page.rawLabel })
          : `#${page.pageNumber}`
      );
    }
    return map;
  }, [result, t]);

  const rejections = useMemo(() => {
    // Per-page verdicts are the current record; a page's own reason belongs
    // under that page. `page_notes` below still supplies the legacy rejects.
    const pageEntries: RejectionEntry[] = (result.qa_page_reviews ?? [])
      .filter(
        (r) =>
          r.verdict === 'rejected' &&
          (r.notes?.trim().length ?? 0) > 0 &&
          r.page_number >= 1 &&
          (pageNumber === undefined || r.page_number === pageNumber)
      )
      .map((r) => ({
        review: {
          tier: r.tier,
          reviewed_at: r.reviewed_at,
          created_at: r.reviewed_at,
          reviewer: r.reviewer
        } as OcrQaReview,
        pageLabel: pageLabelByNumber.get(r.page_number) ?? `#${r.page_number}`,
        text: r.notes!
      }));

    const reviews = (result.qa_reviews ?? [])
      .filter((r) => r.verdict === 'rejected')
      .sort(
        (a, b) =>
          new Date(b.reviewed_at ?? b.created_at).getTime() -
          new Date(a.reviewed_at ?? a.created_at).getTime()
      );

    // A page already covered by a per-page row must not be listed twice.
    const covered = new Set(
      (result.qa_page_reviews ?? []).map((r) => r.page_number)
    );

    const entries: RejectionEntry[] = [...pageEntries];
    for (const review of reviews) {
      // The shared note is about the container, not one page.
      if (includeSharedNotes && (review.notes?.trim().length ?? 0) > 0) {
        entries.push({ review, pageLabel: null, text: review.notes! });
      }
      for (const [pageNumberKey, text] of Object.entries(
        review.page_notes ?? {}
      )) {
        if (!text?.trim()) continue;
        const key = Number(pageNumberKey);
        // A key that is not a real page number cannot be shown against one.
        if (!Number.isInteger(key) || key < 1) continue;
        if (covered.has(key)) continue;
        if (pageNumber !== undefined && key !== pageNumber) continue;
        const pageLabel = pageLabelByNumber.get(key) ?? `#${pageNumberKey}`;
        entries.push({ review, pageLabel, text });
      }
    }
    return entries;
  }, [
    result.qa_reviews,
    result.qa_page_reviews,
    pageLabelByNumber,
    pageNumber,
    includeSharedNotes
  ]);

  if (rejections.length === 0) return null;

  return (
    <div className='mt-3 space-y-2 rounded-lg border border-red-500/30 bg-red-500/5 p-3'>
      <div className='flex items-center gap-1.5 text-xs font-semibold text-red-600 dark:text-red-400'>
        <MessageSquareWarning className='h-3.5 w-3.5' />
        {t('rejectionCommentsTitle')}
      </div>
      <ul className='space-y-2'>
        {rejections.map(({ review, pageLabel, text }, i) => (
          <li
            key={`${review.id}-${pageLabel ?? 'general'}-${i}`}
            className='space-y-1'
          >
            <div className='text-muted-foreground flex flex-wrap items-center gap-1.5 text-[10px]'>
              <Badge
                variant='outline'
                className='border-red-500/30 bg-red-500/10 text-[10px] text-red-600'
              >
                {t('tier')} {review.tier}
              </Badge>
              {pageLabel && (
                <Badge variant='secondary' className='text-[10px]'>
                  {pageLabel}
                </Badge>
              )}
              {review.reviewer?.name ? (
                <span>
                  {t('commentByReviewer', { name: review.reviewer.name })}
                </span>
              ) : null}
              <span className='ms-auto'>
                {new Date(
                  review.reviewed_at ?? review.created_at
                ).toLocaleString()}
              </span>
            </div>
            <p className='text-sm break-words whitespace-pre-wrap'>{text}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
